/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useUpdateBelegung } from '@/domain/dienstplan/features/VisualEditor/hooks/useBelegungen/useUpdateBelegungen';
import { useVisualEditor } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/useVisualEditor';
import { formatHourDecimalToTimeString } from '@/domain/dienstplan/features/VisualEditor/utils/timeHelper/formatHourDecimalToTimeString';
import { transformTimeStringToMinutes } from '@/domain/dienstplan/features/VisualEditor/utils/timeHelper/transformTimeStringToMinutes';
import { useVisualEditorContext } from '@/domain/dienstplan/features/VisualEditor/VisualEditorContext';
import {
  Belegung,
  BelegungsIntervall,
} from '@/domain/dienstplan/shared/types/Belegung';
import {
  MITARBEITER_THRESHOLD,
  STUNDEN_MAX,
} from '@/domain/dienstplan/shared/utils/constants';
import { useTranslation } from 'react-i18next';
import z from 'zod';
import { useForm as useTanStackForm } from '@tanstack/react-form';
import { toast } from '@/hooks/use-toast';

function calculateDauer(beginnTimeString: string, endeTimeString: string) {
  const beginn = transformTimeStringToMinutes(beginnTimeString);
  const ende = transformTimeStringToMinutes(endeTimeString);
  if (ende !== 0) {
    return ende - beginn;
  } else {
    return 24 * 60 - beginn;
  }
}

export function useForm({
  rects,
  popoverRect,
  setOpen,
}: {
  rects: BelegungsIntervall[];
  popoverRect: Belegung;
  setOpen: (state: boolean) => void;
}) {
  const { t } = useTranslation();
  const { maxMitarbeiterAnzahl } = useVisualEditorContext();
  const [updatedBelegung] = useUpdateBelegung();

  const { store } = useVisualEditor();
  const offset = store.ui.xAxisOffset;

  const defaultBelegungInput = {
    beginn: popoverRect.start,
    ende: popoverRect.ende,
    anzahl: popoverRect.personenanzahlMax,
  };

  const formSchema = z
    .object({
      beginn: z.number(
        t(
          'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.beginnNotEmpty',
        ),
      ),
      ende: z.number(
        t(
          'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.endeNotEmpty',
        ),
      ),
      anzahl: z.number(
        t(
          'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.anzahlMitarbeitende',
        ),
      ),
    })
    .superRefine((data, ctx) => {
      const isBeginnIntersectBelegung =
        rects.filter(
          (interval) =>
            interval.belegung.ende <= popoverRect.start &&
            data.beginn < interval.belegung.ende &&
            popoverRect.id !== interval.belegung.id &&
            popoverRect.arbeitsbereich.id ===
              interval.belegung.arbeitsbereich.id,
        ).length > 0;

      const isEndeIntersectBelegung =
        rects.filter(
          (interval) =>
            interval.belegung.start >= popoverRect.ende &&
            data.ende > interval.belegung.start &&
            popoverRect.id !== interval.belegung.id &&
            popoverRect.arbeitsbereich.id ===
              interval.belegung.arbeitsbereich.id,
        ).length > 0;

      if (isBeginnIntersectBelegung) {
        ctx.addIssue({
          code: 'custom',
          path: ['beginn'],
          message: t(
            'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.beginn',
          ),
        });
      }

      if (isEndeIntersectBelegung) {
        ctx.addIssue({
          code: 'custom',
          path: ['ende'],
          message: t(
            'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.ende',
          ),
        });
      }

      if (data.anzahl > maxMitarbeiterAnzahl.value + MITARBEITER_THRESHOLD) {
        ctx.addIssue({
          code: 'custom',
          message: t(
            'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.anzahlMitarbeitendeZuHoch',
            {
              count: data.anzahl,
              maxCount: maxMitarbeiterAnzahl.value,
            },
          ),
          path: ['anzahl'],
        });
      }

      if (data.anzahl < 1) {
        ctx.addIssue({
          code: 'custom',
          message: t(
            'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.anzahlMitarbeitende',
          ),
          path: ['anzahl'],
        });
      }

      if (data.ende > STUNDEN_MAX) {
        ctx.addIssue({
          code: 'custom',
          message: t(
            'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.endeNachTageswechsel',
          ),
          path: ['ende'],
        });
      }

      if (data.ende < data.beginn && data.ende !== 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['beginn'],
          message: t(
            'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.endeVorBeginn',
          ),
        });
      }

      const dauer = calculateDauer(
        formatHourDecimalToTimeString(data.beginn - offset, offset),
        formatHourDecimalToTimeString(data.ende - offset, offset),
      );

      if (dauer < 15) {
        ctx.addIssue({
          code: 'custom',
          path: ['beginn'],
          message: t(
            'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.mindestDauer',
          ),
        });
      }
    });

  const form = useTanStackForm({
    defaultValues: defaultBelegungInput,
    validators: {
      onChange: formSchema,
    },
    onSubmit: async ({ value }) => {
      updatedBelegung({
        input: {
          id: popoverRect.id,
          von: formatHourDecimalToTimeString(value.beginn - offset, offset),
          anzahlMitarbeiter: value.anzahl,
          dauer: calculateDauer(
            formatHourDecimalToTimeString(value.beginn - offset, offset),
            formatHourDecimalToTimeString(value.ende - offset, offset),
          ),
        },
      }).then(() => {
        const newBelegung = new Belegung({
          ...popoverRect,
          ende: value.ende,
          start: value.beginn,
          personenanzahlMax: value.anzahl,
        });
        const newInterval = new BelegungsIntervall(newBelegung, 'inactive');
        store.updateBelegungById(newInterval, newBelegung.id);

        setOpen(false);
        toast({
          title: t(
            'domain.dienstplan.mindestbelegung.visualEditor.popover.updateSuccess',
          ),
        });
      });
    },
  });

  return form;
}
